import React, { useCallback, useEffect, useLayoutEffect, useState, useRef, useMemo } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Autocomplete,
  TextField,
  ListSubheader,
  IconButton,
  Popover,
  Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import TodayIcon from '@mui/icons-material/Today';
import CloseIcon from '@mui/icons-material/Close';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { useTheme } from '@mui/material/styles';
import http from '../../services/http';
import CalendarEventDialog from '../../components/CalendarEventDialog';
import MonthCalendarGrid from '../../components/MonthCalendarGrid';
import {
  getMonthGridRange,
  startOfLocalDay,
  eventOverlapsLocalDay,
  formatEventRange,
} from '../../utils/calendarUtils';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import {
  AGENDA_SWATCHES,
  contrastTextForBg,
  loadAgendaColorsMap,
  saveAgendaColorsMap,
  pickUnusedSwatch,
  loadAgendaOverlayUserIds,
  saveAgendaOverlayUserIds,
} from '../../utils/agendaColors';

const AgendaPage = ({ currentUser }) => {
  const theme = useTheme();
  const history = useHistory();
  const now = new Date();
  const [operatorsList, setOperatorsList] = useState([]);
  const [viewAnchor, setViewAnchor] = useState(() => ({
    y: now.getFullYear(),
    m: now.getMonth(),
  }));
  const [selectedDate, setSelectedDate] = useState(() => startOfLocalDay(new Date()));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [googleStatus, setGoogleStatus] = useState({ configured: false, connected: false, email: null });
  const [overlayUserIds, setOverlayUserIds] = useState([]);
  const skipNextOverlaySaveRef = useRef(false);
  const lastHydratedOverlayUserIdRef = useRef(null);
  const [agendaColorsByKey, setAgendaColorsByKey] = useState({});
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null);
  const [colorPickerKey, setColorPickerKey] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dialogReadOnly, setDialogReadOnly] = useState(false);
  const [initialSlotStart, setInitialSlotStart] = useState(null);
  const [initialSlotEnd, setInitialSlotEnd] = useState(null);

  useEffect(() => {
    http
      .get('/users/list')
      .then((res) => setOperatorsList(Array.isArray(res.data) ? res.data : []))
      .catch(() => setOperatorsList([]));
  }, []);

  const loadGoogleStatus = useCallback(() => {
    http
      .get('/google-calendar/status')
      .then((res) => setGoogleStatus(res.data || { configured: false, connected: false, email: null }))
      .catch(() => setGoogleStatus({ configured: false, connected: false, email: null }));
  }, []);

  useEffect(() => {
    loadGoogleStatus();
    // Trata o retorno do fluxo OAuth do Google (?google=connected|error).
    const params = new URLSearchParams(window.location.search || '');
    const g = params.get('google');
    if (g === 'connected') {
      toast.success('Google Agenda conectado.');
      history.replace('/agenda');
      loadGoogleStatus();
    } else if (g === 'error') {
      toast.error('Não foi possível conectar ao Google Agenda.');
      history.replace('/agenda');
    }
  }, [loadGoogleStatus, history]);

  const handleConnectGoogle = useCallback(async () => {
    try {
      const { data } = await http.get('/google-calendar/connect');
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Integração com o Google Agenda indisponível.');
    }
  }, []);

  const handleDisconnectGoogle = useCallback(async () => {
    if (!window.confirm('Desconectar sua conta do Google Agenda? Novos eventos deixarão de sincronizar.')) return;
    try {
      await http.post('/google-calendar/disconnect');
      toast.success('Google Agenda desconectado.');
      loadGoogleStatus();
    } catch (e) {
      toast.error('Não foi possível desconectar.');
    }
  }, [loadGoogleStatus]);

  useLayoutEffect(() => {
    if (currentUser == null) {
      setOverlayUserIds([]);
      lastHydratedOverlayUserIdRef.current = null;
      skipNextOverlaySaveRef.current = true;
      return;
    }
    const uid = currentUser.id;
    if (uid == null || uid === '') {
      return;
    }
    const uidStr = String(uid);
    if (lastHydratedOverlayUserIdRef.current === uidStr) {
      return;
    }
    lastHydratedOverlayUserIdRef.current = uidStr;
    const myId = Number(uid);
    const fromStorage = loadAgendaOverlayUserIds(uid).filter((id) => Number(id) !== myId);
    setOverlayUserIds(fromStorage);
    skipNextOverlaySaveRef.current = true;
  }, [currentUser]);

  useEffect(() => {
    if (currentUser?.id == null || currentUser?.id === '') return;
    if (skipNextOverlaySaveRef.current) {
      skipNextOverlaySaveRef.current = false;
      return;
    }
    saveAgendaOverlayUserIds(currentUser.id, overlayUserIds);
  }, [overlayUserIds, currentUser?.id]);

  useEffect(() => {
    if (currentUser?.id == null) return;
    setAgendaColorsByKey(loadAgendaColorsMap(currentUser.id));
  }, [currentUser?.id]);

  useEffect(() => {
    if (currentUser?.id == null) return;
    setAgendaColorsByKey((prev) => {
      if (prev.me) return prev;
      return { ...prev, me: theme.palette.primary.main };
    });
  }, [currentUser?.id, theme.palette.primary.main]);

  useEffect(() => {
    if (currentUser?.id == null) return;
    setAgendaColorsByKey((prev) => {
      let changed = false;
      const next = { ...prev };
      overlayUserIds.forEach((id) => {
        const k = String(id);
        if (!next[k]) {
          next[k] = pickUnusedSwatch(Object.values(next));
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [overlayUserIds, currentUser?.id]);

  useEffect(() => {
    if (currentUser?.id == null) return;
    if (Object.keys(agendaColorsByKey).length === 0) return;
    saveAgendaColorsMap(currentUser.id, agendaColorsByKey);
  }, [agendaColorsByKey, currentUser?.id]);

  const { gridStart, gridEnd } = useMemo(
    () => getMonthGridRange(viewAnchor.y, viewAnchor.m),
    [viewAnchor.y, viewAnchor.m]
  );

  const monthTitle = useMemo(() => {
    const raw = new Date(viewAnchor.y, viewAnchor.m, 1).toLocaleDateString('pt-BR', {
      month: 'long',
      year: 'numeric',
    });
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }, [viewAnchor.y, viewAnchor.m]);

  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const mySeq = ++loadSeq.current;
    setLoading(true);
    try {
      const params = { from: gridStart.toISOString(), to: gridEnd.toISOString() };
      const myId = Number(currentUser?.id);
      const overlayIds = overlayUserIds
        .map((id) => Number(id))
        .filter((id) => id > 0 && id !== myId);

      const requests = [
        http.get('/calendar-events', { params }),
        ...overlayIds.map((uid) =>
          http.get(`/calendar-events/for-user/${uid}`, { params }).catch(() => ({ data: [] }))
        ),
      ];
      const results = await Promise.all(requests);
      if (mySeq !== loadSeq.current) return;

      const mine = Array.isArray(results[0].data) ? results[0].data : [];
      const merged = mine.map((ev) => ({ ...ev, _agendaKey: 'me' }));
      const seen = new Set(merged.map((e) => e.id));

      for (let i = 1; i < results.length; i += 1) {
        const uid = overlayIds[i - 1];
        const list = Array.isArray(results[i].data) ? results[i].data : [];
        for (const ev of list) {
          if (ev.visibility !== 'public') continue;
          if (seen.has(ev.id)) continue;
          seen.add(ev.id);
          merged.push({ ...ev, _agendaKey: String(uid) });
        }
      }

      setEvents(merged);
    } catch (err) {
      if (mySeq !== loadSeq.current) return;
      setEvents([]);
      if (err.response?.status === 429) {
        toast.warning('Muitas requisições. Aguarde um instante e atualize a página.');
      }
    } finally {
      if (mySeq === loadSeq.current) {
        setLoading(false);
      }
    }
  }, [gridStart, gridEnd, overlayUserIds, currentUser?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const isMine = (ev) => Number(ev.user_id) === Number(currentUser?.id);

  const resetDialogSlots = () => {
    setInitialSlotStart(null);
    setInitialSlotEnd(null);
  };

  const openNew = () => {
    resetDialogSlots();
    setEditing(null);
    setDialogReadOnly(false);
    setDialogOpen(true);
  };

  const openNewForDay = (dayDate) => {
    const s = startOfLocalDay(dayDate);
    s.setHours(9, 0, 0, 0);
    const e = new Date(s.getTime() + 60 * 60 * 1000);
    setInitialSlotStart(s.toISOString());
    setInitialSlotEnd(e.toISOString());
    setEditing(null);
    setDialogReadOnly(false);
    setDialogOpen(true);
  };

  const openEdit = (ev) => {
    if (ev.source === 'ticket_sla' && ev.ticket_id) {
      history.push(`/ticket/${ev.ticket_id}`);
      return;
    }
    resetDialogSlots();
    setEditing(ev);
    setDialogReadOnly(!isMine(ev));
    setDialogOpen(true);
  };

  const goPrevMonth = () => {
    setViewAnchor(({ y, m }) => (m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }));
  };

  const goNextMonth = () => {
    setViewAnchor(({ y, m }) => (m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }));
  };

  const goThisMonth = () => {
    const t = new Date();
    setViewAnchor({ y: t.getFullYear(), m: t.getMonth() });
    setSelectedDate(startOfLocalDay(t));
  };

  const dayEvents = selectedDate
    ? events.filter((ev) => eventOverlapsLocalDay(ev, selectedDate)).sort((a, b) => {
        const t1 = new Date(a.starts_at).getTime();
        const t2 = new Date(b.starts_at).getTime();
        return t1 - t2;
      })
    : [];

  const overlayAutocompleteOptions = useMemo(() => {
    const myId = Number(currentUser?.id);
    return (operatorsList || [])
      .filter(
        (u) =>
          Number(u?.id) !== myId &&
          !overlayUserIds.some((oid) => Number(oid) === Number(u.id))
      )
      .sort((a, b) => {
        const groupA = a.service_group?.description || 'Sem departamento';
        const groupB = b.service_group?.description || 'Sem departamento';
        if (groupA !== groupB) {
          return groupA.localeCompare(groupB);
        }
        return (a.name || '').localeCompare(b.name || '');
      });
  }, [operatorsList, overlayUserIds, currentUser?.id]);

  const orderedOverlayUsers = useMemo(() => {
    const byId = new Map((operatorsList || []).map((u) => [Number(u.id), u]));
    return overlayUserIds.map((id) => byId.get(Number(id))).filter(Boolean);
  }, [overlayUserIds, operatorsList]);

  const removeOverlayUser = (userId) => {
    setOverlayUserIds((prev) => prev.filter((id) => Number(id) !== Number(userId)));
  };

  const openColorPicker = (e, key) => {
    setColorPickerAnchor(e.currentTarget);
    setColorPickerKey(key);
  };

  const closeColorPicker = () => {
    setColorPickerAnchor(null);
    setColorPickerKey(null);
  };

  const applyAgendaColor = (hex) => {
    if (colorPickerKey == null) return;
    setAgendaColorsByKey((prev) => ({ ...prev, [colorPickerKey]: hex }));
    closeColorPicker();
  };

  const colorHexForAgendaKey = useCallback(
    (key) => agendaColorsByKey[key] || agendaColorsByKey.me || theme.palette.primary.main,
    [agendaColorsByKey, theme.palette.primary.main]
  );

  const getEventChipSx = useCallback(
    (ev) => {
      const key = ev._agendaKey != null ? String(ev._agendaKey) : 'me';
      const hex = colorHexForAgendaKey(key);
      const fg = contrastTextForBg(hex);
      return {
        bgcolor: `${hex} !important`,
        color: `${fg} !important`,
      };
    },
    [colorHexForAgendaKey]
  );

  const sidePanelText = {
    title: {
      mb: 0.5,
      fontWeight: 600,
      color: `${theme.palette.text.primary} !important`,
    },
    body: {
      display: 'block',
      mb: 1.5,
      color: `${theme.palette.text.secondary} !important`,
      '& strong': {
        color: `${theme.palette.text.primary} !important`,
      },
    },
    sectionLabel: {
      mt: 2,
      mb: 0.75,
      display: 'block',
      fontWeight: 600,
      color: `${theme.palette.text.primary} !important`,
    },
    emptyHint: {
      display: 'block',
      color: `${theme.palette.text.secondary} !important`,
    },
  };

  return (
    <Grid sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          px: '1.5rem',
          py: '1rem',
          backgroundColor: theme.palette.background.default,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, color: `${theme.palette.text.primary} !important` }}>
          Agenda
        </Typography>
      </Box>

      <Box sx={{ px: '1.5rem', py: 2, minWidth: 0, flex: 1, overflow: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
        <Paper
          sx={{
            p: 2,
            width: { xs: '100%', md: 320 },
            flexShrink: 0,
            position: { md: 'sticky' },
            top: { md: 8 },
            alignSelf: 'flex-start',
            maxHeight: { md: 'calc(100vh - 80px)' },
            overflowY: 'auto',
            bgcolor: `${theme.palette.background.paper} !important`,
            color: `${theme.palette.text.primary} !important`,
          }}
        >
          <Typography variant="subtitle2" sx={sidePanelText.title}>
            Sobrepor agendas
          </Typography>
          <Typography variant="caption" sx={sidePanelText.body}>
            Sua agenda completa permanece visível. Adicione colegas para ver na grade apenas os compromissos{' '}
            <strong>públicos</strong> deles, junto com os seus.
          </Typography>
          <Autocomplete
            multiple
            noOptionsText="Nenhuma opção"
            sx={{ width: '100%' }}
            options={overlayAutocompleteOptions}
            groupBy={(option) => option.service_group?.description || 'Sem departamento'}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option.name || option.email || '';
            }}
            filterOptions={(options, state) => {
              const inputValue = state.inputValue.toLowerCase().trim();
              if (!inputValue) return options;
              return options.filter((option) => {
                const name = (option.name || '').toLowerCase();
                const email = (option.email || '').toLowerCase();
                return name.includes(inputValue) || email.includes(inputValue);
              });
            }}
            value={orderedOverlayUsers}
            onChange={(_, newValue) => {
              setOverlayUserIds(newValue.map((u) => u.id));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar pessoas"
                placeholder="Nome ou e-mail..."
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: `${theme.palette.background.default} !important`,
                    color: `${theme.palette.text.primary} !important`,
                  },
                  '& .MuiOutlinedInput-input': {
                    color: `${theme.palette.text.primary} !important`,
                  },
                  '& .MuiInputLabel-root': {
                    color: `${theme.palette.text.secondary} !important`,
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: `${theme.palette.primary.main} !important`,
                  },
                }}
              />
            )}
            renderTags={() => null}
            renderGroup={(params) => (
              <li key={params.key}>
                <ListSubheader
                  sx={{
                    backgroundColor: `${
                      theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100]
                    } !important`,
                    fontWeight: 'bold',
                    fontSize: '12px',
                    color: `${theme.palette.text.primary} !important`,
                    py: 0.5,
                    lineHeight: 1.4,
                  }}
                >
                  {params.group}
                </ListSubheader>
                {params.children}
              </li>
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: `${theme.palette.text.primary} !important` }}
                  >
                    {option.name || 'Sem nome'}
                  </Typography>
                  {option.email && (
                    <Typography
                      variant="caption"
                      sx={{ color: `${theme.palette.text.secondary} !important` }}
                    >
                      {option.email}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
          />

          <Typography variant="caption" sx={sidePanelText.sectionLabel}>
            Cores das agendas
          </Typography>
          <Typography variant="caption" sx={{ ...sidePanelText.emptyHint, mb: 1, mt: -0.5 }}>
            Toque no quadrado para escolher a cor de cada faixa na grade.
          </Typography>
          <ListItem
            dense
            sx={{
              px: 1,
              py: 0.75,
              pr: 6,
              mb: 1,
              borderRadius: 1,
              border: (t) => `1px solid ${t.palette.divider}`,
              bgcolor: `${theme.palette.action.hover} !important`,
            }}
            secondaryAction={
              <IconButton
                size="small"
                aria-label="Cor da minha agenda"
                onClick={(e) => openColorPicker(e, 'me')}
                sx={{ mt: 0.25 }}
              >
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: 1,
                    bgcolor: colorHexForAgendaKey('me'),
                    border: (t) => `1px solid ${t.palette.divider}`,
                  }}
                />
              </IconButton>
            }
          >
            <ListItemText
              primary="Minha agenda"
              secondary="Compromissos, convites e SLA"
              primaryTypographyProps={{
                variant: 'body2',
                sx: { color: `${theme.palette.text.primary} !important` },
              }}
              secondaryTypographyProps={{
                variant: 'caption',
                sx: { color: `${theme.palette.text.secondary} !important` },
              }}
            />
          </ListItem>

          <Typography variant="caption" sx={sidePanelText.sectionLabel}>
            Agendas sobrepostas
          </Typography>
          {orderedOverlayUsers.length === 0 ? (
            <Typography variant="caption" sx={sidePanelText.emptyHint}>
              Nenhuma pessoa selecionada. Use o campo acima para adicionar.
            </Typography>
          ) : (
            <List dense disablePadding sx={{ mt: 0.5 }}>
              {orderedOverlayUsers.map((u) => (
                <ListItem
                  key={u.id}
                  sx={{
                    px: 1,
                    py: 0.75,
                    pr: 10,
                    mb: 0.5,
                    borderRadius: 1,
                    border: (t) => `1px solid ${t.palette.divider}`,
                    bgcolor: `${theme.palette.action.hover} !important`,
                  }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, pr: 0.25 }}>
                      <IconButton
                        size="small"
                        aria-label={`Cor da agenda de ${u.name || u.email}`}
                        onClick={(e) => openColorPicker(e, String(u.id))}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: 1,
                            bgcolor: colorHexForAgendaKey(String(u.id)),
                            border: (t) => `1px solid ${t.palette.divider}`,
                          }}
                        />
                      </IconButton>
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label={`Remover agenda de ${u.name || u.email}`}
                        onClick={() => removeOverlayUser(u.id)}
                        sx={{
                          color: `${theme.palette.text.secondary} !important`,
                          '&:hover': { color: `${theme.palette.error.main} !important` },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <EventAvailableIcon
                      fontSize="small"
                      sx={{ color: `${colorHexForAgendaKey(String(u.id))} !important` }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={u.name || u.email || 'Usuário'}
                    secondary={u.email || undefined}
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: { color: `${theme.palette.text.primary} !important` },
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      sx: { color: `${theme.palette.text.secondary} !important` },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Popover
            open={Boolean(colorPickerAnchor)}
            anchorEl={colorPickerAnchor}
            onClose={closeColorPicker}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { mt: 0.5, p: 0 } }}
          >
            <Box
              sx={{
                p: 1.5,
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 1,
                width: 200,
              }}
            >
              {AGENDA_SWATCHES.map((c) => (
                <Box
                  key={c}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      applyAgendaColor(c);
                    }
                  }}
                  onClick={() => applyAgendaColor(c)}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: c,
                    cursor: 'pointer',
                    border: '2px solid transparent',
                    boxSizing: 'border-box',
                    '&:hover': { borderColor: 'primary.main' },
                    '&:focus-visible': { outline: (t) => `2px solid ${t.palette.primary.main}` },
                  }}
                />
              ))}
            </Box>
          </Popover>
        </Paper>

        <Paper sx={{ p: 2, flex: 1, minWidth: 0, width: '100%' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" onClick={goPrevMonth} aria-label="Mês anterior">
                <ChevronLeftIcon />
              </Button>
              <Typography variant="subtitle1" sx={{ minWidth: 200, textAlign: 'center', fontWeight: 600 }}>
                {monthTitle}
              </Typography>
              <Button size="small" onClick={goNextMonth} aria-label="Próximo mês">
                <ChevronRightIcon />
              </Button>
              <Button size="small" variant="outlined" startIcon={<TodayIcon />} onClick={goThisMonth}>
                Hoje
              </Button>
            </Box>

            <Box sx={{ flex: 1, minWidth: 160 }} />

            {googleStatus.connected ? (
              <Tooltip title={googleStatus.email ? `Conectado como ${googleStatus.email}` : 'Conectado ao Google Agenda'}>
                <Button size="small" variant="outlined" color="success" onClick={handleDisconnectGoogle}>
                  Google Agenda conectado
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title={googleStatus.configured ? 'Conectar sua conta do Google Agenda' : 'Requer configuração das credenciais do Google no servidor'}>
                <Button size="small" variant="outlined" startIcon={<EventAvailableIcon />} onClick={handleConnectGoogle}>
                  Conectar Google Agenda
                </Button>
              </Tooltip>
            )}

            <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
              Novo compromisso
            </Button>
          </Box>

          <MonthCalendarGrid
            viewYear={viewAnchor.y}
            viewMonthIndex={viewAnchor.m}
            events={events}
            selectedDate={selectedDate}
            onSelectDay={(d) => setSelectedDate(startOfLocalDay(d))}
            onAddOnDay={(d) => {
              setSelectedDate(startOfLocalDay(d));
              openNewForDay(d);
            }}
            onEventClick={(ev) => openEdit(ev)}
            loading={loading}
            getEventChipSx={getEventChipSx}
          />

          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                {selectedDate.toLocaleDateString('pt-BR', {
                  weekday: 'long',
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })}
              </Typography>
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => openNewForDay(selectedDate)}>
                Novo neste dia
              </Button>
            </Box>

            {dayEvents.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Nada agendado neste dia. Use o + no canto do dia na grade ou &quot;Novo neste dia&quot; para criar. A grade do mês
                permanece visível mesmo sem compromissos.
              </Typography>
            ) : (
              <List disablePadding sx={{ mt: 1 }}>
                {dayEvents.map((ev) => (
                  <ListItem
                    key={ev.id}
                    sx={{
                      mb: 1,
                      borderRadius: 1,
                      bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                      cursor: 'pointer',
                    }}
                    onClick={() => openEdit(ev)}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Typography
                            variant="body1"
                            component="span"
                            sx={{
                              pl: 1,
                              borderLeft: `4px solid ${colorHexForAgendaKey(String(ev._agendaKey ?? 'me'))}`,
                              flex: 1,
                              minWidth: 120,
                            }}
                          >
                            {ev.title}
                          </Typography>
                          {ev.source === 'ticket_sla' && (
                            <Chip size="small" color="secondary" variant="outlined" label="SLA" />
                          )}
                          <Chip size="small" label={ev.visibility === 'public' ? 'Público' : 'Privado'} />
                          {ev.owner?.name && <Chip size="small" variant="outlined" label={ev.owner.name} />}
                          {/*
                            * O TICKET do compromisso (ponto 3 da Gisele,
                            * 15/10/2026). A agenda já nasce de dentro do
                            * chamado, mas a tela não dizia de qual -- para
                            * saber, alguém escrevia o número na descrição.
                            *
                            * `stopPropagation` porque a linha inteira abre a
                            * EDIÇÃO: sem ele, clicar no chip abriria o
                            * diálogo em vez de levar ao chamado, que é
                            * justamente o contrário do que o chip promete.
                            */}
                          {ev.ticket?.id && (
                            <Chip
                              size="small"
                              color="primary"
                              variant="outlined"
                              data-testid="chip-do-ticket"
                              label={`Chamado #${ev.ticket.number}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                history.push(`/ticket/${ev.ticket.id}`);
                              }}
                              sx={{ cursor: 'pointer' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={formatEventRange(ev.starts_at, ev.ends_at, ev.all_day)}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Paper>
        </Box>

        <CalendarEventDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setEditing(null);
            setDialogReadOnly(false);
            resetDialogSlots();
          }}
          onSaved={load}
          event={editing}
          users={operatorsList}
          currentUserId={currentUser?.id}
          defaultTicketId={null}
          readOnly={dialogReadOnly}
          initialSlotStart={initialSlotStart}
          initialSlotEnd={initialSlotEnd}
        />
      </Box>
    </Grid>
  );
};

const mapState = (state) => ({
  currentUser: state.user.currentUser,
});

export default connect(mapState)(AgendaPage);
